import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { ClassReportType, HighlightedProps } from '../../types';
import { SectionLayout, getFilteredAverage, getOthersAverage } from '../modules';
import { StudentProgress } from '../../../components';
import { FIXED_AXIS } from '../../../components/charts/helpers/vars';

type Props = {
  classReport: ClassReportType;
  showOthersData: boolean
  hideAverage?: boolean
  chartIsExpanded?: boolean
} & HighlightedProps;

const QualityLearningTime = ({
  classReport,
  showOthersData,
  highlightedLabel,
  highlightedIds,
  hideAverage,
  chartIsExpanded,
  onElementClick,
}: Props): JSX.Element => {
  const { translate } = useI18n();

  const roundToDecimal = (value: number): string => {
    return Number.isSafeInteger(value) ? value.toFixed(0) : value.toFixed(2);
  };

  const value = roundToDecimal(classReport.qualityLearningTimeAverage);
  const highlightedAverage = highlightedLabel
    ? getFilteredAverage(classReport.students.all, highlightedIds, (student) => student.chartsData.mathsAge > 0 ? student.chartsData.qualityLearningTime : null)
    : null;
  const othersAverage = showOthersData
    ? getOthersAverage(classReport.students.all, highlightedIds, (student) => student.chartsData.mathsAge > 0 ? student.chartsData.qualityLearningTime : null)
    : null;

  return (
    <SectionLayout
      title={translate('pages.REPORTS.SECTIONS.QUALITY.title')}
      value={value}
      highlightLabel={highlightedLabel}
      highlightValue={highlightedAverage != null ? roundToDecimal(highlightedAverage) : null}
      othersLabel={showOthersData && highlightedIds.length > 0 ? translate('pages.REPORTS.HEADERS.OTHERS') : null}
      othersValue={othersAverage != null ? roundToDecimal(othersAverage) : null}
      unit={translate('pages.REPORTS.SECTIONS.QUALITY.unit', {
        count: parseInt(value),
      })}
      description={translate('pages.REPORTS.SECTIONS.QUALITY.description')}
      moreInfo={translate('pages.REPORTS.SECTIONS.QUALITY.info')}
      className={'pdf-no-break'}
      data-cy="quality-learning-time"
      hideAverage={hideAverage}
      chartIsExpanded={chartIsExpanded}
      id='quality-learning-time-container'
    >
      <StudentProgress
        showOthersData={showOthersData}
        data={classReport.students.all ?? []}
        chartType={'qualityLearningTime'}
        highlightedIds={highlightedIds}
        removeUnassessed={true}
        customAxis={FIXED_AXIS.recent?.qualityLearningTime}
        onElementClick={onElementClick}
      />
    </SectionLayout>
  );
};

QualityLearningTime.displayName = 'QualityLearningTime';

export default QualityLearningTime;
